/**
 * @author Lorenzo Muscariello <lmuscariello@ciaopeople.com>
 */
import { Action, EventHandler, SimpleEventHandler } from "@ciaopeople/cp-shatsu/EventHandler";
import { ExecutionId } from "../player/MediaResource.js";
import { VideoContext } from "../player/Player.js";
import { AdBreakType, AdUnitType, VASTTagAdUnit } from "./AdUnit.js";
import { AdsEnrichedVideoContext, NullAdsEnrichedVideoContext } from "./AdsEnrichedVideoContext.js";
import { isPlayerAdEvent, PlayerAdBreakRelatedEvent, PlayerAdEvent, PlayerAdEventAction, PlayerAdEventListener, PlayerAdEventType } from "./PlayerAdEvent.js";
import "./ima/types/ima.js";
import { AD_EVENT_NAME } from "./ima/AbstractImaController.js";
import { VideoInfoRawData } from "../../model/video/VideoInfo.js";



/**
 * Objects implementing this interface are responsibile for Ad Management
 */
export interface AdsController {

    /**
     * Called on Media Player Start.
     * Implementations can override this method to perform needed operations
     * before ad operations start and just after media player init.
     */
    onStart(): Promise<any>
    /**
     * This method wraps the original `VideoContext` object released by the player
     * with its `AdsEnrichedVideoContext` implementation.
     * @param ctx original video context to wrap
     */
    getAdAndWrapContext(ctx: VideoContext, willAutoplay: boolean): Promise<AdsEnrichedVideoContext>;


    onAdEvent(listener: PlayerAdEventListener): PlayerAdEventAction;
    offAdEvent(listener: PlayerAdEventAction): void;




}





/**
 * Null implementation for `AdsController`
 */
export class NullAdsController implements AdsController {
    
    private events: EventHandler;

    constructor() {
        this.events = new SimpleEventHandler();
    }
    onStart(): Promise<any> {
        return Promise.resolve();
    }
    getAdAndWrapContext(ctx: VideoContext): Promise<AdsEnrichedVideoContext> {
        return Promise.resolve(new NullAdsEnrichedVideoContext(this,ctx));
    }

    onAdEvent(listener: PlayerAdEventListener): PlayerAdEventAction {
        const rListener: Action = (name: string, evt: any) => {
            try {
                if (isPlayerAdEvent(evt)) listener(evt);
            } catch (e) {
                
            }
        }
        
        this.events.on(AD_EVENT_NAME,rListener);
        return rListener;
    }
    offAdEvent(listener: PlayerAdEventAction): void {
        this.events.off(listener);
    }

    public signalResumableMedia(breakType: AdBreakType, info: VideoInfoRawData, executionId: ExecutionId): void {

        const fakeAdUnit: VASTTagAdUnit = {
            breakType: breakType,
            label: "no_preroll",
            baseUrl: "no_ads_url",
            unitType: AdUnitType.VAST_TAG
        }

        const toTrigger: PlayerAdBreakRelatedEvent = {
            eventType: PlayerAdEventType.AD_RESUMABLE_MEDIA,
            executionId: executionId,
            mediaId: info.id,
            adUnit: fakeAdUnit,
            adBreak: fakeAdUnit
        }
        this.triggerAdEvent(toTrigger)
    }

    protected triggerAdEvent(evt: PlayerAdEvent): void {
        this.events.trigger(AD_EVENT_NAME,evt);
    }

}

/**
 * UI Elements and related controls composing the target HTML5 container for ads
 */
export type AdContainerControls = {

    
    readonly fullscreen: boolean;
    container: HTMLElement;
    customClickLayer?: HTMLElement,
    show(adInfo: google.ima.Ad): void;
    hide(): void;
    hideCustomClickLayer(): void;
    showCustomClickLayer(): void;
    signalPlayStatus(newStatus: boolean): void;
    signalAudioStatus(newStatus: boolean): void;
    refresh(): void;
    showCloseControl(): void;
    hideCloseControl(): void;
    onNextCloseClick(listner: () => void): void;
    signalAdProgress(time: number, duration: number): void;
    signalAdWaiting(isWaiting: boolean);
}